//
// # Box Draw Base
//
// Shared logic for modes that draw a rectangle on screen by tracking
// an anchor point and a moving endpoint (box-select, zoom-to-box, etc.).
//

// Local Imports
import { model } from "GenAlphaModel";
import type { InteractionHost } from "../interaction_host";
import { ModeHandler } from "./base";
import { DrawTo } from "DrawCore/src/draw";
import * as style from "DrawCore/src/style";

// Semi-transparent blue fill for the box rectangle
const BOX_FILL = style.Color.new(100, 150, 255, 0.15);
// Solid blue stroke for the box rectangle border
const BOX_STROKE = style.Color.new(100, 150, 255, 0.8);

// Style for the box rectangle
const boxStyle: style.ShapeStyle = {
  cap: "round",
  join: "round",
  linewidth: 1,
  fill: BOX_FILL,
  stroke: BOX_STROKE,
};

// # BoxDraw
//
// Base class for modes that track a rectangular region on screen.
// Handles anchor/endpoint tracking, rectangle drawing, and mouse-move updates.
// Subclasses implement `finalize()` to define what happens when the box is accepted.
//
export abstract class BoxDraw extends ModeHandler {
  protected endpoint: model.Point;

  constructor(
    readonly editor: InteractionHost,
    protected readonly anchor: model.Point,
    protected readonly statusKey: string,
    status: string
  ) {
    super();
    this.endpoint = { ...anchor };
    if (status) {
      editor.overlays.setStatusMessage(status, statusKey);
    }
  }

  // Update endpoint to current mouse position.
  updateEndpoint(): void {
    this.endpoint = { ...this.editor.uiState.mousePos.sch };
  }

  // Compute the bounding box from anchor and endpoint.
  protected getBbox(): model.Bbox {
    return model.Bbox.fromTwoPoints([this.anchor, this.endpoint]);
  }

  // Update endpoint to current mouse position and trigger redraw
  override handleMouseMove = () => {
    this.updateEndpoint();
    return this.editor.requestRedraw();
  };

  override abort = () => {
    this.editor.overlays.clearStatusMessage(this.statusKey);
    this.editor.goUiIdle();
    return this.editor.requestRedraw();
  };

  // Subclasses define what happens when the box is accepted.
  abstract finalize(): void;

  // Draw the rectangle on the canvas.
  drawRect = (to: DrawTo): void => {
    const { anchor, endpoint } = this;
    const scale = this.editor.canvas.worldTransforms.schToPage.scale;
    const bbox: model.Bbox = {
      min: {
        x: Math.min(anchor.x, endpoint.x),
        y: Math.min(anchor.y, endpoint.y),
      },
      max: {
        x: Math.max(anchor.x, endpoint.x),
        y: Math.max(anchor.y, endpoint.y),
      },
    };
    to.bbox(bbox, { ...boxStyle, linewidth: boxStyle.linewidth / scale });
  };

  // `draw` is the ModeHandler hook called by the render loop.
  override draw = (to: DrawTo): void => {
    this.drawRect(to);
  };
}

// # BoxDrawWait
//
// Base class for keyboard-entry box modes that start immediately at the current
// mouse position. Left-click to accept, right-click/Esc to cancel.
// Subclasses provide the concrete BoxDraw instance via `createInner`.
//
export abstract class BoxDrawWait extends ModeHandler {
  protected abstract readonly inner: BoxDraw;

  // Move updates the box rectangle endpoint.
  override handleMouseMove = () => {
    this.inner.updateEndpoint();
    return this.inner.editor.requestRedraw();
  };

  override handleMouseDown = (e: MouseEvent) => {
    if (e.button === 0) {
      // Left-click: accept.
      this.inner.finalize();
    } else {
      // Right-click (or other): cancel.
      this.abort();
    }
  };

  override abort = () => {
    this.inner.abort();
  };

  // Delegate drawing to the inner BoxDraw.
  override draw = (to: DrawTo): void => {
    this.inner.drawRect(to);
  };
}
